import { PropertyInferenceData, defaultPropertyDefinitions } from "@buildwithflux/constants";
import { getTerminals } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
import { chain, groupBy, mapValues } from "lodash";
// TODO we treat property.name / default.label as a key practically everywhere; we
// should strengthen that assumption and simply make it the keys of these mappings.
// For both perf and clarity.
const PROPERTY_NAME_TO_KEY_MAPPING = Object.fromEntries(Object.entries(defaultPropertyDefinitions).map(([key, value]) => [
    value.label,
    key,
]));
// Some properties (system defaults? known properties?) get their key names as uid, instead
// of a Guid. I'm not 100% clear on what the determining factor is, but it's important
// to prevent duplicate properties, which breaks things (at least in our current codebase).
const PROPERTIES_WITH_LABEL_UIDS = Object.fromEntries(Object.values(defaultPropertyDefinitions).map((p) => [p.label, p.key]));
export class PropertyInference {
    static makeProperty(name, value) {
        // TODO we should make this function or its equiv. part of the standard document
        // api, and use it around the place eg in PropertyEditorDialog.tsx
        const propertyKey = PROPERTY_NAME_TO_KEY_MAPPING[name];
        if (!propertyKey) {
            throw new Error(`Property name ${name} is not recognized`);
        }
        const defaultProperty = defaultPropertyDefinitions[propertyKey];
        const defaults = {};
        if (defaultProperty) {
            defaults.dynamicAutoComplete = defaultProperty.dynamic_auto_complete;
            defaults.unit = defaultProperty.unit ?? "";
            defaults.valueType = defaultProperty.valueType;
        }
        const uid = PROPERTIES_WITH_LABEL_UIDS[name] ?? guid();
        return {
            ...defaults,
            uid,
            value,
            name,
            order: -1,
        };
    }
    static inferPropertiesAndApplyToDocument(document) {
        const properties = PropertyInference.inferPartProperties(document);
        for (const [name, value] of Object.entries(properties.partProperties)) {
            const property = PropertyInference.makeProperty(name, value);
            document.properties[property.uid] = property;
        }
        for (const [uid, elementProperties] of Object.entries(properties.elementProperties)) {
            const element = document.elements[uid];
            if (!element)
                continue;
            for (const [name, value] of Object.entries(elementProperties)) {
                const property = PropertyInference.makeProperty(name, value);
                element.properties[property.uid] = property;
            }
        }
    }
    /** Infer properties for this part and any of its sub-components or terminals */
    static inferPartProperties(part) {
        const elementProperties = {};
        Object.assign(elementProperties, PropertyInference.inferPinProperties(part));
        const partProperties = {};
        return {
            // Properties at the part's top level:
            partProperties,
            // Properties belonging to the part's elements:
            elementProperties,
        };
    }
    static inferPinProperties(part) {
        const terminals = PropertyInference.getTerminals(part);
        const tokenizedNames = mapValues(terminals, (t) => PropertyInference.tokenizePinName(t.name ?? ""));
        const pinGroupings = {};
        // First, detect buses and assign properties accordingly.
        // Note: I don't love the 4+ levels of loop nesting here, but it's not as bad as it looks,
        // because most of the loops are small N. It still adds up!
        const maxTokens = Math.max(...Object.values(tokenizedNames).map((t) => t.length));
        for (let tokenLevel = maxTokens - 1; tokenLevel >= 0; tokenLevel--) {
            // Group names so we get groups like [ABC_SCL3, ABC_SDA3] or [ABC_SCL_G1, ABC_SDA_G1]
            const groupedByCommonName = groupBy(Object.entries(tokenizedNames)
                .filter(([_, tokens]) => tokens.length > tokenLevel)
                .map(([uid, tokens]) => ({ uid, tokens })), ({ tokens }) => tokens
                .filter((_, idx) => idx != tokenLevel)
                .map((t) => t.token)
                .join("_") +
                "_" +
                (tokens[tokenLevel]?.number ?? ""));
            for (const bus of Object.values(PropertyInferenceData.buses)) {
                // Find if all required pins for this bus exist within any namegroup. If so, assign a Pin Group
                // of `busName` to all of them. If multiple namegroups have a bus match, then use the number to distinguish,
                for (const [_commonName, groupOfPins] of Object.entries(groupedByCommonName)) {
                    const primaryNames = Object.keys(bus.pins);
                    const variations = [primaryNames, ...(bus.variations ?? [])];
                    let found;
                    for (const variation of variations) {
                        if (variation.length !== primaryNames.length) {
                            // We hardcode these values, so this should never happen. But fail loudly if it does,
                            // so we're sure to catch any mess-ups we make.
                            throw new Error("Variation pin count must match primary pin count");
                        }
                        const matches = variation
                            .map((v) => groupOfPins.find((n) => !(n.uid in pinGroupings) && n.tokens[tokenLevel]?.name === v))
                            .filter((m) => !!m);
                        if (matches.length === variation.length) {
                            found = matches.map((m, idx) => ({ ...m, spec: bus.pins[primaryNames[idx]] }));
                            break;
                        }
                    }
                    if (!found || !found[0])
                        continue;
                    const tokenChain = found[0].tokens.slice();
                    const preTokens = tokenChain.slice(0, tokenLevel);
                    const currentToken = tokenChain[tokenLevel];
                    const postTokens = tokenChain.slice(tokenLevel + 1);
                    let busNumber = "";
                    if (postTokens.length) {
                        // If the bus number is encoded like I2C_SDA_A or I2C_SDA_G2
                        busNumber = postTokens.map((t) => t.token).join("_");
                    }
                    if (currentToken.number) {
                        // If the bus number is encoded like I2C_SDA2
                        busNumber += currentToken.number;
                    }
                    if (preTokens.at(-1)?.name === bus.busName) {
                        const busNameToken = preTokens.pop();
                        if (busNameToken.number) {
                            // If the bus number is encoded like I2C2_SDA.
                            busNumber += busNameToken.number;
                        }
                    }
                    for (const busPin of found) {
                        const props = {
                            "Pin Group": busNumber ? `${bus.busName}${busNumber}` : bus.busName,
                            // TODO should we also set the Bus Group property here?
                        };
                        const section = preTokens.map((t) => t.token).join("_");
                        if (section) {
                            props["Section"] = section;
                        }
                        else if (busPin.spec.defaultSection) {
                            props["Section"] = busPin.spec.defaultSection;
                        }
                        if (busPin.spec.type) {
                            props["Pin Type"] = busPin.spec.type;
                        }
                        pinGroupings[busPin.uid] = props;
                    }
                }
            }
        }
        const allNonBusPinNameCounts = chain(tokenizedNames)
            .pickBy((_t, uid) => !(uid in pinGroupings))
            .values()
            .countBy((tokens) => tokens.map((t) => t.token).join("_"))
            .value();
        // For any remaining pins that don't match known buses, match them against known standalone pins.
        // If no pin spec matches, then use the tokens as generic pin groupings where it makes sense.
        for (const [uid, tokens] of Object.entries(tokenizedNames)) {
            if (uid in pinGroupings)
                continue;
            const lastToken = tokens.at(-1);
            if (!lastToken)
                continue;
            const tokenChain = tokens.slice();
            const name = lastToken.name ?? "";
            const props = (pinGroupings[uid] = {});
            for (const [pattern, spec] of Object.entries(PropertyInferenceData.commonPinSpecs)) {
                if (!new RegExp(`^${pattern}$`).test(name))
                    continue;
                if (spec.defaultSection) {
                    props["Section"] = spec.defaultSection;
                }
                if (spec.type) {
                    props["Pin Type"] = spec.type;
                }
            }
            const fullName = tokens.map((t) => t.token).join("_");
            if ((allNonBusPinNameCounts[fullName] ?? 1) <= 1) {
                // If the pin name is fully unique, the last token is the specific pin name
                // (else: we put multiple same-named pins in a single Pin Group)
                tokenChain.pop();
            }
            const pinGroup = tokenChain.pop()?.token;
            const pinSection = tokenChain.pop()?.token;
            if (pinGroup) {
                props["Pin Group"] = pinGroup;
            }
            if (pinSection) {
                props["Section"] = pinSection;
            }
        }
        // Apply default pin types based on part type, to any that are unassigned:
        const partTypeProperty = Object.values(part.properties).find((p) => p.name === defaultPropertyDefinitions.part_type.label);
        const partType = partTypeProperty?.value;
        const defaultPinType = partType ? PropertyInferenceData.partTypes[partType]?.defaultPinType : undefined;
        if (defaultPinType) {
            for (const uid of Object.keys(terminals)) {
                if (!pinGroupings[uid]) {
                    pinGroupings[uid] = {};
                }
                if (!("Pin Type" in pinGroupings[uid])) {
                    pinGroupings[uid]["Pin Type"] = defaultPinType;
                }
            }
        }
        return pinGroupings;
    }
    static tokenizePinName(pinName) {
        const separators = " _/\\\\";
        const busNames = Object.values(PropertyInferenceData.buses).map((b) => b.busName);
        const busPinNames = Object.values(PropertyInferenceData.buses).flatMap((b) => Object.keys(b.pins).concat(...(b.variations ?? [])));
        const knownLabels = Object.keys(PropertyInferenceData.commonPinSpecs).concat(busNames).concat(busPinNames);
        const patterns = {
            num: `[-+]?[0-9]+(\\.[0-9]+)?`,
            alpha: `[A-Za-z]+`,
            labels: knownLabels.map((s) => `(${s})`).join("|"),
            // There are probably ~infinitely many chars that are used as some kind of modifier.
            // They'll mostly be captured by our "unknown characters" handling below. Including
            // a few explicit ones here mainly as examples.
            modifiers: `[-+><~#!]`,
        };
        // TODO maybe we need to have a different set of regexes for matching the final token
        // (specific pin identifier) separately from the preceding/parent tokens? The final
        // token is more likely to have patterns like +/- and other things that mean different
        // things in the context of the final token.
        const tokenRegexes = [
            // List more specific regexes first; if a name matches something specific, we'll
            // count that as a match and not look any further. This allows us to eg match things
            // like "I2CSDA" as [I2C, SDA] instead of [I2, CSDA].
            `(?<mods>${patterns.modifiers})?(?<name>${patterns.labels})`, // Match labels with trailing numbers like "3V3" first
            `(?<mods>${patterns.modifiers})(?<name>${patterns.labels})(?<number>${patterns.num})?`,
            `(?<mods>${patterns.modifiers})(?<name>${patterns.alpha})(?<number>${patterns.num})?`,
            `(?<name>${patterns.labels})(?<number>${patterns.num})?(?<mods>${patterns.modifiers})?`,
            `(?<name>${patterns.alpha})(?<number>${patterns.num})?(?<mods>${patterns.modifiers})?`,
            // For names like "CH_1" or "Terminal 1" or "IN +", associate the number or modifier with
            // the same token:
            `(?<name>${patterns.labels})[${separators}]((?<number>${patterns.num})|(?<mods>${patterns.modifiers}))`,
            `(?<name>${patterns.alpha})[${separators}]((?<number>${patterns.num})|(?<mods>${patterns.modifiers}))`,
            // If pin is just a number on its own, with optional modifier, then the number is in fact its name.
            `(?<name>${patterns.num})(?<mods>${patterns.modifiers})?`,
        ];
        let remainder = pinName;
        const tokens = [];
        while (remainder) {
            let token = undefined;
            for (const regex of tokenRegexes) {
                // TODO if the regex is a perf issue here, check whether precompiling them
                // is notably faster.
                const fullRegex = new RegExp(`(?<token>${regex})([${separators}]?)$`);
                const match = remainder.match(fullRegex);
                if (match) {
                    remainder = remainder.slice(0, match.index);
                    // Just get the name match, without separators:
                    token = match.groups;
                    if (!("token" in token) || !("name" in token)) {
                        // Should never happen because we hardcode the regexes, but fail loudly in case
                        // we ever mess them up.
                        throw new Error("Token regex must contain both 'token' and 'name' groups");
                    }
                    tokens.unshift(token);
                    break;
                }
            }
            if (!token) {
                // TODO we should collect the unknown characters and prob include them as flags
                // or other part of the next pin name?
                remainder = remainder.slice(0, -1);
            }
        }
        return tokens;
    }
    static getTerminals(part) {
        return "elements" in part ? getTerminals(part.elements) : part.terminals;
    }
}
//# sourceMappingURL=PropertyInference.js.map